import React, { useState, useEffect } from 'react';
import { usersAPI, departmentsAPI } from '../api/client';
import { usePermissions } from '../context/PermissionsContext';
import apiClient from '../api/client';
import { Users, Building2, Plus, X, Search, AlertCircle, CheckCircle, ShieldCheck, Star } from 'lucide-react';

export default function UserDepartments() {
  const { hasPermission } = usePermissions();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDepartments, setUserDepartments] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [setPrimary, setSetPrimary] = useState(false);
  const [canApproveMap, setCanApproveMap] = useState({}); // deptId → bool
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, assigned, unassigned
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const canManage = hasPermission('manage_users');

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll({ page: 1, pageSize: 1000 });
      setUsers(response.data.items || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      showMessage('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await departmentsAPI.getAll();
      const depts = Array.isArray(response.data?.departments) 
        ? response.data.departments 
        : Array.isArray(response.data) 
          ? response.data 
          : [];
      console.log('Departments fetched:', depts); // Debug log
      setDepartments(depts);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setDepartments([]);
    }
  };

  const fetchUserDepartments = async (userId) => {
    try {
      const response = await usersAPI.getDepartments(userId);
      setUserDepartments(response.data || []);
    } catch (err) {
      console.error('Error fetching user departments:', err);
      setUserDepartments([]);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    fetchUserDepartments(user.UserID);
  };

  const handleOpenAssignModal = () => {
    setSelectedDepts([]);
    setSetPrimary(false);
    setCanApproveMap({});
    setShowAssignModal(true);
  };

  const handleDeptToggle = (deptId) => {
    setSelectedDepts(prev => {
      const next = prev.includes(deptId) ? prev.filter(id => id !== deptId) : [...prev, deptId];
      // Clear canApprove entry when dept is deselected
      if (prev.includes(deptId)) {
        setCanApproveMap(m => { const c = { ...m }; delete c[deptId]; return c; });
      }
      return next;
    });
  };

  const handleAssignDepartments = async () => {
    if (selectedDepts.length === 0) {
      showMessage('Please select at least one department', 'error');
      return;
    }

    try {
      setSaving(true);
      await usersAPI.assignDepartments(selectedUser.UserID, {
        departmentIds: selectedDepts,
        isPrimary: setPrimary,
        canApproveMap  // { deptId: bool }
      });
      
      showMessage(`Assigned ${selectedDepts.length} department(s) successfully`, 'success');
      setShowAssignModal(false);
      fetchUserDepartments(selectedUser.UserID);
      fetchUsers(); // Refresh to update department counts
    } catch (err) {
      console.error('Error assigning departments:', err);
      showMessage(err.response?.data?.error || 'Failed to assign departments', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDepartment = async (deptId) => {
    if (!confirm('Remove this user from the department?')) return;

    try {
      await usersAPI.removeDepartment(selectedUser.UserID, deptId);
      showMessage('User removed from department successfully', 'success');
      fetchUserDepartments(selectedUser.UserID);
      fetchUsers(); // Refresh to update department counts
    } catch (err) {
      console.error('Error removing department:', err);
      showMessage('Failed to remove department', 'error');
    }
  };

  const handleToggleCanApprove = async (dept) => {
    try {
      await apiClient.patch(`/users/${selectedUser.UserID}/departments/${dept.DepartmentID}`, {
        canApprove: !dept.CanApprove
      });
      fetchUserDepartments(selectedUser.UserID);
    } catch (err) {
      console.error('Error toggling CanApprove:', err);
      showMessage('Failed to update approval permission', 'error');
    }
  };

  const handleSetPrimaryExisting = async (dept) => {
    try {
      await apiClient.patch(`/users/${selectedUser.UserID}/departments/${dept.DepartmentID}`, {
        isPrimary: true
      });
      fetchUserDepartments(selectedUser.UserID);
      showMessage(`${dept.DepartmentName} set as primary`, 'success');
    } catch (err) {
      console.error('Error setting primary:', err);
      showMessage('Failed to set primary department', 'error');
    }
  };

  const showMessage = (text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.Email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.FullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.Username?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filterStatus === 'all' ||
      (filterStatus === 'assigned' && (user.DepartmentCount > 0)) ||
      (filterStatus === 'unassigned' && (!user.DepartmentCount || user.DepartmentCount === 0));
    
    return matchesSearch && matchesFilter;
  });

  const unassignedCount = users.filter(u => !u.DepartmentCount || u.DepartmentCount === 0).length;
  const assignedCount = users.filter(u => u.DepartmentCount > 0).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Users className="w-7 h-7" />
          User Department Assignments
        </h1>
        <p className="text-gray-600 mt-1">Manage user assignments to departments</p>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="text-sm text-gray-600">Total Users</div>
          <div className="text-2xl font-bold text-gray-900">{users.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="text-sm text-gray-600">Assigned to Departments</div>
          <div className="text-2xl font-bold text-gray-900">{assignedCount}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
          <div className="text-sm text-gray-600">Unassigned</div>
          <div className="text-2xl font-bold text-gray-900">{unassignedCount}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users List */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Users</h2>
            
            {/* Search and Filter */}
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setFilterStatus('all')}
                  className={`btn btn-sm ${
                    filterStatus === 'all'
                      ? 'btn-primary'
                      : 'btn-secondary'
                  }`}
                >
                  All ({users.length})
                </button>
                <button
                  onClick={() => setFilterStatus('assigned')}
                  className={`btn btn-sm ${
                    filterStatus === 'assigned'
                      ? 'btn-primary'
                      : 'btn-secondary'
                  }`}
                >
                  Assigned ({assignedCount})
                </button>
                <button
                  onClick={() => setFilterStatus('unassigned')}
                  className={`btn btn-sm ${
                    filterStatus === 'unassigned'
                      ? 'btn-primary'
                      : 'btn-secondary'
                  }`}
                >
                  Unassigned ({unassignedCount})
                </button>
              </div>
            </div>
          </div>

          <div className="max-h-[600px] overflow-y-auto">
            {filteredUsers.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No users found
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredUsers.map(user => (
                  <div
                    key={user.UserID}
                    onClick={() => handleSelectUser(user)}
                    className={`p-4 cursor-pointer transition-colors ${
                      selectedUser?.UserID === user.UserID
                        ? 'bg-primary-50 border-l-4 border-primary-600'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{user.FullName || user.Email}</div>
                        <div className="text-sm text-gray-600">{user.Email}</div>
                        <div className="text-sm text-gray-500">{user.RoleName || 'No role'}</div>
                      </div>
                      <div className="text-right">
                        {user.DepartmentCount > 0 ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {user.DepartmentCount} dept{user.DepartmentCount !== 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Unassigned
                          </span>
                        )}
                      </div>
                    </div>
                    {user.Departments && (
                      <div className="mt-1 text-xs text-gray-500">
                        {user.Departments}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* User Departments */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedUser ? `${selectedUser.FullName || selectedUser.Email}'s Departments` : 'Departments'}
              </h2>
              {selectedUser && canManage && (
                <button
                  onClick={handleOpenAssignModal}
                  className="btn btn-primary btn-sm"
                >
                  <Plus className="w-4 h-4" />
                  Assign
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[600px] overflow-y-auto">
            {!selectedUser ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>Select a user to view their departments</p>
              </div>
            ) : userDepartments.length === 0 ? (
              <div className="p-8 text-center">
                <Building2 className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p className="text-gray-500 mb-4">Not assigned to any department</p>
                {canManage && (
                  <button
                    onClick={handleOpenAssignModal}
                    className="text-primary-600 hover:underline"
                  >
                    Assign departments
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {userDepartments.map(dept => (
                  <div key={dept.UserDepartmentID} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
                          <span className="font-medium text-gray-900">{dept.DepartmentName}</span>
                          {dept.IsPrimary && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                              Primary
                            </span>
                          )}
                          {dept.CanApprove && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded flex items-center gap-1">
                              <ShieldCheck className="w-3 h-3" /> Can Approve
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Assigned {new Date(dept.AssignedAt).toLocaleDateString()}
                          {dept.AssignedByEmail && ` by ${dept.AssignedByEmail}`}
                        </div>
                        {canManage && (
                          <div className="flex items-center gap-3 mt-2">
                            <button
                              onClick={() => handleToggleCanApprove(dept)}
                              className={`text-xs flex items-center gap-1 px-2 py-1 rounded border transition-colors ${
                                dept.CanApprove
                                  ? 'border-green-300 text-green-700 hover:bg-red-50 hover:border-red-300 hover:text-red-700'
                                  : 'border-gray-300 text-gray-600 hover:bg-green-50 hover:border-green-300 hover:text-green-700'
                              }`}
                              title={dept.CanApprove ? 'Revoke approval permission' : 'Grant approval permission'}
                            >
                              <ShieldCheck className="w-3 h-3" />
                              {dept.CanApprove ? 'Revoke Approver' : 'Grant Approver'}
                            </button>
                            {!dept.IsPrimary && (
                              <button
                                onClick={() => handleSetPrimaryExisting(dept)}
                                className="text-xs flex items-center gap-1 px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
                                title="Set as primary department"
                              >
                                <Star className="w-3 h-3" /> Set Primary
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {canManage && (
                        <button
                          onClick={() => handleRemoveDepartment(dept.DepartmentID)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors shrink-0"
                          title="Remove from department"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assign Departments Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Assign Departments</h3>
              <button onClick={() => setShowAssignModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Select departments to assign to <strong>{selectedUser?.FullName || selectedUser?.Email}</strong>
              </p>

              <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
                {Array.isArray(departments) && departments.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No departments available</p>
                )}
                {Array.isArray(departments) && departments.map(dept => {
                  const alreadyAssigned = userDepartments.some(ud => ud.DepartmentID === dept.DepartmentID);
                  const isSelected = selectedDepts.includes(dept.DepartmentID);
                  return (
                    <div
                      key={dept.DepartmentID}
                      className={`rounded border transition-colors ${
                        alreadyAssigned
                          ? 'bg-gray-50 border-gray-200 opacity-50'
                          : 'border-gray-200'
                      }`}
                    >
                      <label className={`flex items-center p-3 ${alreadyAssigned ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => !alreadyAssigned && handleDeptToggle(dept.DepartmentID)}
                          disabled={alreadyAssigned}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-3 text-sm text-gray-900">
                          {dept.DepartmentName}
                          {alreadyAssigned && <span className="text-gray-500 ml-2">(Already assigned)</span>}
                        </span>
                      </label>
                      {isSelected && (
                        <div className="px-3 pb-3 pt-0">
                          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!canApproveMap[dept.DepartmentID]}
                              onChange={(e) => setCanApproveMap(m => ({ ...m, [dept.DepartmentID]: e.target.checked }))}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <ShieldCheck className="w-4 h-4 text-green-600" />
                            Can approve invoices in this department
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={setPrimary}
                    onChange={(e) => setSetPrimary(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Set as primary department (clears other primary flags)
                  </span>
                </label>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="btn btn-secondary"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignDepartments}
                  className="btn btn-primary"
                  disabled={saving || selectedDepts.length === 0}
                >
                  {saving ? 'Assigning...' : `Assign ${selectedDepts.length} Dept${selectedDepts.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
